import { Component, HostBinding, inject } from '@angular/core';
import { PageComponent, Transfer, sleep } from '@tytapp/common';
import { ApiArticle, ApiArticleFranchise, ApiFranchise, ApiProduction, PagesApi } from '@tytapp/api';
import { ActivatedRoute } from '@angular/router';
import { ProductionsService } from '@tytapp/common-ui';
import { combineLatest } from 'rxjs';
import { environment } from '@tytapp/environment';
import { isClientSide } from '@tytapp/environment-utils';
import { CanonicalURLService } from '../../../../common/canonical-url.service';
import { ArticlesService, FranchisesService } from '@tytapp/articles';

@Component({
    selector: 'tyt-articles',
    templateUrl: './articles.component.html',
    styleUrls: ['./articles.component.scss']
})
export class ArticlesComponent extends PageComponent {
    private articlesService = inject(ArticlesService);
    private franchisesService = inject(FranchisesService);
    private productionsService = inject(ProductionsService);
    private pagesApi = inject(PagesApi);
    private canonicalUrls = inject(CanonicalURLService);

    @HostBinding('class.animate-in')
    animateIn = false;

    @Transfer()
    franchises: ApiFranchise[];

    @Transfer()
    recentEpisodes: ApiProduction[];

    @Transfer()
    recentArticles: ApiArticle[] = [];

    @Transfer()
    ready: boolean = false;

    @Transfer()
    articles: ApiArticle[];

    @Transfer()
    franchise: ApiArticleFranchise;

    @Transfer()
    featuredArticle: ApiArticle;

    @Transfer()
    earlierArticles: ApiArticle[];

    // Paging
    @Transfer()
    total: number;
    @Transfer()
    page: number;
    @Transfer()
    size: number;

    @Transfer()
    franchiseId: string;

    goToPage(page) {
        this.router.navigate([], { queryParams: { page } });
    }

    get pageComponentId() {
        return 'articles';
    }

    async init() {
        let observer = combineLatest(this.route.params, this.route.queryParams, (params, queries) => ({ params, queries }));
        observer.subscribe(this.update.bind(this));

        if (!this.hasServerState) {
            this.recentEpisodes = await this.productionsService.latestVODs(0, 1);
        }

        if (isClientSide())
            await sleep(1);
        this.animateIn = true;

        this.franchises = (await this.franchisesService.all()).filter(x => x.show_in_original_reporting);
    }

    async update({ params, queries }) {
        if (this.hasServerState)
            return;

        this.pageTitle = 'Reports';
        this.size = 15;

        this.franchise = null;
        this.articles = null;
        this.ready = false;

        if (queries.page) {
            this.page = queries.page;
        } else {
            this.page = 1;
        }

        if (params.franchiseId) {
            this.franchiseId = params.franchiseId;

            this.franchise = await this.franchisesService.get(params.franchiseId);
            if (!this.franchise) {
                let article = await this.articlesService.get(params.franchiseId);
                if (article) {
                    this.redirection.go(await this.articlesService.getArticleUrl(article));
                    return;
                }

                this.redirection.go('/reports', true);
                return;
            }
            this.pageTitle = this.franchise.name;

            let pageContent = await this.articlesService.franchiseFeed(params.franchiseId, (this.page - 1) * this.size, this.size);
            (this.franchise as ApiArticleFranchise).articles = pageContent;
            this.total = pageContent.total;

            if (this.canonicalUrls.redirectToCanonical(await this.articlesService.getFranchiseUrl(this.franchise)))
                return;
        } else {
            this.franchiseId = null;

            const articlesPage = await this.pagesApi.getArticlesPage().toPromise();

            this.franchise = articlesPage.featured_franchise;
            [this.featuredArticle] = articlesPage.articles;
            this.recentArticles = articlesPage.articles.slice(1);

            this.articles = this.recentArticles.splice(4);
            this.earlierArticles = this.articles.splice(5);

            this.head.setCanonicalLink(`/reports`);
        }

        this.addRSSDiscoverability();
        this.ready = true;
    }

    urlForFranchise(franchise: ApiFranchise) {
        return this.articlesService.getFranchiseUrlSync(franchise);
    }

    get globalRssUrl() {
        return `${environment.urls.platform}/rss/reports`;
    }

    get rssUrl() {
        return `${
            environment.urls.platform
        }/rss/${
            this.franchise?.show_in_original_reporting ? 'reports' : 'articles'
        }/${
            this.franchise?.identifier || this.franchise?.id
        }`;
    }

    rssLink = null;

    addRSSDiscoverability() {
        if (this.rssLink) {
            this.head.removeLink(this.rssLink);
        }

        this.rssLink = this.head.addRSSLink(this.franchiseId ? this.rssUrl : this.globalRssUrl, this.franchise?.name);
    }
}
